import { type Currency, capitalizeTerm, type StorefrontTerm } from "@loot-os/api-contract";
import { useQuery } from "@tanstack/react-query";

import { spendableCents } from "../../../../lib/balance.js";
import { useCurrency } from "../../../../lib/currency.js";
import { useFavorites } from "../../../../lib/favorites.js";
import { boxesQueryOptions } from "../../../../lib/queries.js";
import { useBalance } from "../../../../lib/shopper.js";
import { useTerminology } from "../../../../lib/terminology.js";
import { type BoxTile, useBoxTiles } from "../../shared/boxTiles.js";
import type { TemplateAction } from "../../shared/links.js";
import { filterBoxes, NEW_BOX_DAYS } from "./filterBoxes.js";
import { type BoxesSearch, type BoxTab, useBoxesSearch } from "./search.js";

export type BoxGridState =
  | { status: "loading" }
  | { status: "error"; message: string }
  /** Nothing to show, and why; `action` is what would change that, when something would. */
  | { status: "empty"; message: string; action: TemplateAction | null }
  | { status: "ready"; tiles: BoxTile[] };

export interface BoxGridContent {
  title: string;
  currency: Currency;
  state: BoxGridState;
}

const TITLES: Record<BoxTab, (plural: string) => string> = {
  all: (plural) => `All ${plural}`,
  new: (plural) => `New ${plural}`,
  favorites: (plural) => `Favorite ${plural}`,
};

/** The grid: GET /v1/boxes through the tab and every filter, each box with its heart. */
export function useBoxGridContent(): BoxGridContent {
  const { box } = useTerminology();
  const currency = useCurrency();
  const [search] = useBoxesSearch();
  const query = useQuery(boxesQueryOptions());
  const favorites = useFavorites();
  const balance = useBalance().data;
  const tab = search.tab ?? "all";

  const shown = query.data
    ? filterBoxes(query.data, {
        tab,
        query: search.q ?? "",
        minCents: search.min ?? null,
        maxCents: search.max ?? null,
        budgetCents: search.enough && balance ? spendableCents(balance) : null,
        favorites: favorites.saved,
        sort: search.sort ?? "default",
        now: Date.now(),
      })
    : null;
  const tiles = useBoxTiles(shown);

  return {
    title: capitalizeTerm(TITLES[tab](box.plural)),
    currency,
    state: gridState({
      box,
      tab,
      search,
      error: query.error,
      listed: query.data?.length ?? null,
      tiles,
      signedIn: favorites.signedIn,
      favoritesLoaded: favorites.loaded,
    }),
  };
}

function gridState(args: {
  box: StorefrontTerm;
  tab: BoxTab;
  search: BoxesSearch;
  error: Error | null;
  /** How many boxes the environment lists; null until they load. */
  listed: number | null;
  tiles: BoxTile[] | null;
  signedIn: boolean;
  favoritesLoaded: boolean;
}): BoxGridState {
  const { box, tab, search, tiles } = args;
  if (args.error && args.listed === null) {
    return { status: "error", message: args.error.message || `failed to load ${box.plural}` };
  }
  if (tab === "favorites" && !args.signedIn) {
    return {
      status: "empty",
      message: `Sign in to see the ${box.plural} you save.`,
      action: { label: "Sign in", to: "/login", emphasis: "primary" },
    };
  }
  // Only the favorites tab waits for the favorites; elsewhere the hearts fill in when they land.
  if (!tiles || (tab === "favorites" && !args.favoritesLoaded)) return { status: "loading" };
  if (tiles.length > 0) return { status: "ready", tiles };
  const filtered =
    Boolean(search.q?.trim()) ||
    search.min !== undefined ||
    search.max !== undefined ||
    search.enough === true;
  const message =
    args.listed === 0
      ? `No active ${box.plural} in this environment yet — publish one in the dashboard.`
      : filtered
        ? `No ${box.plural} match these filters.`
        : tab === "favorites"
          ? `Press the heart on any ${box.singular} to save it here.`
          : `No ${box.plural} were added in the last ${NEW_BOX_DAYS} days.`;
  return { status: "empty", message, action: null };
}
